import { create } from 'zustand';
import { supabase } from './supabase';
import type { FeatureFlags } from '../types';

// Default feature flags as specified in CLAUDE.md
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  // CORE - All ON
  'core.PRACTICE_MODE': true,
  'core.STUDY_PLAN': true,
  'core.SPACED_REPETITION': true,
  'core.WEAK_AREA_FOCUS': true,
  'core.VOICE_STUDY_MODE': true,
  'core.INTERVIEW_PREP': true,

  // GAMIFICATION - All ON
  'gamification.XP_SYSTEM': true,
  'gamification.LEVELS': true,
  'gamification.STREAKS': true,
  'gamification.ACHIEVEMENTS': true,
  'gamification.LEADERBOARDS': true,
  'gamification.WEEKLY_LEAGUES': true,

  // COMMUNITY - All ON
  'community.FORUMS': true,
  'community.STUDY_GROUPS': true,
  'community.QUESTION_COMMENTS': true,

  // GROWTH - Most ON; campaign/referral flows OFF until rollout
  'growth.REFERRAL_PROGRAM': true,
  'growth.PROGRAM_CHALLENGES': true,
  'growth.SHAREABLE_ACHIEVEMENTS': true,
  'growth.PUBLIC_PROFILES': true,
  'growth.CAMPAIGN_TRIALS': false,
  'growth.TRIAL_EMAILS': false,
  'growth.B2B_REFERRALS': false,

  // CREATOR - Mostly ON
  'creator.CONTENT_SUBMISSION': true,
  'creator.PEER_REVIEW': true,
  'creator.PAYMENTS': true,
  'creator.REVENUE_SHARE': true,
  'creator.CME_CREDITS': false, // OFF - Requires ACCME accreditation
  'creator.INTERVIEW_QUESTIONS': false, // OFF until v2 — admin-authored for v1

  // MENTORSHIP - All ON
  'mentorship.VOLUNTEER': true,
  'mentorship.MATCHING': true,
  'mentorship.EXPERT_SESSIONS': true,
  'mentorship.PAYMENTS': true,

  // B2B - All ON
  'b2b.ORG_ACCOUNTS': true,
  'b2b.ORG_DASHBOARD': true,
  'b2b.ORG_ANALYTICS': true,
  'b2b.ORG_CHALLENGES': true,
  'b2b.ORG_SSO': true,

  // ADMIN - All ON
  'admin.CONTENT_CMS': true,
  'admin.USER_MANAGEMENT': true,
  'admin.ANALYTICS': true,
  'admin.FEATURE_FLAGS': true,
};

interface FeatureFlagsState {
  flags: FeatureFlags;
  isLoading: boolean;
  lastSynced: Date | null;
  setFlags: (flags: Partial<FeatureFlags>) => void;
  setFlag: (key: keyof FeatureFlags, value: boolean) => void;
  resetFlags: () => void;
  loadFlags: () => Promise<void>;
  saveFlags: (changes: Record<string, boolean>) => Promise<{ success: boolean; error?: string }>;
}

export const useFeatureFlags = create<FeatureFlagsState>((set, get) => ({
  flags: DEFAULT_FEATURE_FLAGS,
  isLoading: false,
  lastSynced: null,

  setFlags: (newFlags) => {
    set((state) => ({
      flags: { ...state.flags, ...newFlags },
    }));
  },

  setFlag: (key, value) => {
    set((state) => ({
      flags: { ...state.flags, [key]: value },
    }));
  },

  resetFlags: () => {
    set({ flags: DEFAULT_FEATURE_FLAGS });
  },

  loadFlags: async () => {
    set({ isLoading: true });
    try {
      // Fetch from database
      const { data, error } = await supabase
        .from('feature_flags')
        .select('key, value');

      if (error) {
        console.error('Failed to load feature flags from database:', error);
        // Fall back to defaults
        set({ flags: DEFAULT_FEATURE_FLAGS, isLoading: false });
        return;
      }

      if (data && data.length > 0) {
        const dbFlags: Partial<FeatureFlags> = {};
        for (const row of data) {
          dbFlags[row.key as keyof FeatureFlags] = row.value;
        }
        set({
          flags: { ...DEFAULT_FEATURE_FLAGS, ...dbFlags },
          lastSynced: new Date(),
        });
      } else {
        set({ flags: DEFAULT_FEATURE_FLAGS });
      }
    } catch (err) {
      console.error('Error loading feature flags:', err);
      set({ flags: DEFAULT_FEATURE_FLAGS });
    } finally {
      set({ isLoading: false });
    }
  },

  saveFlags: async (changes: Record<string, boolean>) => {
    try {
      // Upsert each changed flag to the database
      const upserts = Object.entries(changes).map(([key, value]) => ({
        key,
        value,
        updated_at: new Date().toISOString(),
      }));

      const { error } = await supabase
        .from('feature_flags')
        .upsert(upserts, { onConflict: 'key' });

      if (error) {
        console.error('Failed to save feature flags:', error);
        return { success: false, error: error.message };
      }

      // Apply changes to local state
      set((state) => ({
        flags: { ...state.flags, ...changes },
        lastSynced: new Date(),
      }));

      return { success: true };
    } catch (err: any) {
      console.error('Error saving feature flags:', err);
      return { success: false, error: err.message };
    }
  },
}));

// Helper hook to check a single flag
export function useFeatureFlag(key: keyof FeatureFlags): boolean {
  return useFeatureFlags((state) => state.flags[key]);
}

// Helper function for conditional rendering
export function isFeatureEnabled(flags: FeatureFlags, key: keyof FeatureFlags): boolean {
  return flags[key];
}

// Feature flag categories for admin UI
export const FEATURE_FLAG_CATEGORIES = {
  core: {
    name: 'Core Features',
    description: 'Essential learning functionality',
    flags: ['core.PRACTICE_MODE', 'core.STUDY_PLAN', 'core.SPACED_REPETITION', 'core.WEAK_AREA_FOCUS', 'core.VOICE_STUDY_MODE'],
  },
  gamification: {
    name: 'Gamification',
    description: 'XP, levels, achievements, and competitions',
    flags: ['gamification.XP_SYSTEM', 'gamification.LEVELS', 'gamification.STREAKS', 'gamification.ACHIEVEMENTS', 'gamification.LEADERBOARDS', 'gamification.WEEKLY_LEAGUES'],
  },
  community: {
    name: 'Community',
    description: 'Social and collaborative features',
    flags: ['community.FORUMS', 'community.STUDY_GROUPS', 'community.QUESTION_COMMENTS'],
  },
  growth: {
    name: 'Growth',
    description: 'Referrals and viral features',
    flags: ['growth.REFERRAL_PROGRAM', 'growth.PROGRAM_CHALLENGES', 'growth.SHAREABLE_ACHIEVEMENTS', 'growth.PUBLIC_PROFILES', 'growth.CAMPAIGN_TRIALS', 'growth.TRIAL_EMAILS', 'growth.B2B_REFERRALS'],
  },
  creator: {
    name: 'Creator Portal',
    description: 'Content creation and monetization',
    flags: ['creator.CONTENT_SUBMISSION', 'creator.PEER_REVIEW', 'creator.PAYMENTS', 'creator.REVENUE_SHARE', 'creator.CME_CREDITS'],
  },
  mentorship: {
    name: 'Mentorship',
    description: 'Mentor matching and sessions',
    flags: ['mentorship.VOLUNTEER', 'mentorship.MATCHING', 'mentorship.EXPERT_SESSIONS', 'mentorship.PAYMENTS'],
  },
  b2b: {
    name: 'B2B Portal',
    description: 'Organization features',
    flags: ['b2b.ORG_ACCOUNTS', 'b2b.ORG_DASHBOARD', 'b2b.ORG_ANALYTICS', 'b2b.ORG_CHALLENGES', 'b2b.ORG_SSO'],
  },
  admin: {
    name: 'Admin Portal',
    description: 'Administrative features',
    flags: ['admin.CONTENT_CMS', 'admin.USER_MANAGEMENT', 'admin.ANALYTICS', 'admin.FEATURE_FLAGS'],
  },
} as const;

export default useFeatureFlags;
